import { useState } from 'react';
import {
  FLEX_LEVELS,
  FRICTION_PHASES,
  OWNERSHIP_TYPES,
  type DdwConfig,
} from '../config';
import {
  adoptionConfidence,
  fmtMoney,
  projectVendorTax,
  valueAtRisk,
} from '../calc';
import type { DdwLens, DdwResearch, DdwVendor } from '../types';

interface Props {
  lens: DdwLens;
  vendors: DdwVendor[];
  config: DdwConfig;
  research: DdwResearch | null;
  onReviseLens(): void;
  onAddVendor(name: string, category: string): void;
  onRemoveVendor(vendorId: string): void;
  onScoreVendor(vendorId: string): void;
  onOpenReport(mode: 'single' | 'compare' | 'playbook', vendorId?: string): void;
  onSubmitResearch(answers: DdwResearch): void;
  onDismissResearch(): void;
}

/**
 * The Vendor Hub — landing screen after Phase 1 completes. Combines a
 * lens summary, the optional research opt-in panel, the vendor list
 * with score chips, and the Compare / Master playbook actions.
 */
export function VendorHub({
  lens,
  vendors,
  config,
  research,
  onReviseLens,
  onAddVendor,
  onRemoveVendor,
  onScoreVendor,
  onOpenReport,
  onSubmitResearch,
  onDismissResearch,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');

  const scoredCount = vendors.filter((v) => v.scored).length;
  const pendingCount = vendors.length - scoredCount;
  const meta =
    vendors.length === 0
      ? '0 added'
      : pendingCount > 0
        ? `${pendingCount} vendor${pendingCount === 1 ? '' : 's'} need${pendingCount === 1 ? 's' : ''} scoring →`
        : `${vendors.length} added · all scored`;

  const commitAdd = () => {
    const n = name.trim();
    if (!n) return;
    onAddVendor(n, category.trim());
    setName('');
    setCategory('');
    setAdding(false);
  };

  return (
    <div className="ddw-hub-wrap">
      <header className="ddw-hub-header">
        <span className="ddw-hub-tag">Phase 2 · Score vendors</span>
        <h1 className="ddw-hub-title">Your evaluation lens is ready.</h1>
        <p className="ddw-hub-sub">
          Add the vendors you're evaluating. Score each in about two minutes against the lens you
          just built. Compare side-by-side when you're ready.
        </p>
      </header>

      <section className="ddw-hub-section">
        <div className="ddw-hub-section-header">
          <span className="ddw-hub-section-title">Your Lens</span>
          <button type="button" className="ddw-tbtn" onClick={onReviseLens}>
            Revise
          </button>
        </div>
        <LensSummary lens={lens} />
      </section>

      <section className="ddw-hub-section">
        <ResearchPanel
          research={research}
          questions={config.research}
          onSubmit={onSubmitResearch}
          onDismiss={onDismissResearch}
        />
      </section>

      <section className="ddw-hub-section">
        <div className="ddw-hub-section-header">
          <span className="ddw-hub-section-title">Vendors</span>
          <span className={`ddw-hub-section-meta${pendingCount > 0 ? ' is-action' : ''}`}>{meta}</span>
        </div>

        {adding && (
          <div className="ddw-add-vendor-form">
            <div className="ddw-add-vendor-title">Add a vendor</div>
            <div className="ddw-add-vendor-grid">
              <div>
                <label htmlFor="ddw-new-vendor-name">Vendor name</label>
                <input
                  id="ddw-new-vendor-name"
                  type="text"
                  className="ddw-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Ironclad"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitAdd();
                  }}
                />
              </div>
              <div>
                <label htmlFor="ddw-new-vendor-category">Category (your words)</label>
                <input
                  id="ddw-new-vendor-category"
                  type="text"
                  className="ddw-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., CLM, Matter Mgmt, e-billing"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitAdd();
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="ddw-tbtn" onClick={() => setAdding(false)}>
                  Cancel
                </button>
                <button type="button" className="ddw-sbtn" onClick={commitAdd}>
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {vendors.length === 0 && !adding ? (
          <div className="ddw-vendor-empty">
            <div className="ddw-vendor-empty-title">No vendors yet — start with 2 or 3.</div>
            <div className="ddw-vendor-empty-sub">
              A side-by-side comparison only becomes meaningful with at least two. Add the vendors
              you're actively considering.
            </div>
            <button type="button" className="ddw-sbtn" onClick={() => setAdding(true)}>
              + Add your first vendor
            </button>
          </div>
        ) : (
          <>
            <div className="ddw-vendor-list">
              {vendors.map((v) => (
                <VendorCard
                  key={v.id}
                  vendor={v}
                  lens={lens}
                  config={config}
                  onScore={onScoreVendor}
                  onRemove={onRemoveVendor}
                  onReport={() => onOpenReport('single', v.id)}
                />
              ))}
            </div>
            {!adding && (
              <div style={{ marginTop: 14 }}>
                <button type="button" className="ddw-tbtn" onClick={() => setAdding(true)}>
                  + Add a vendor
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <div className="ddw-hub-footer">
        <button
          type="button"
          className="ddw-tbtn"
          onClick={() => onOpenReport('compare')}
          disabled={scoredCount < 2}
        >
          Compare vendors
        </button>
        <button
          type="button"
          className="ddw-tbtn"
          onClick={() => onOpenReport('playbook')}
          disabled={scoredCount < 1}
        >
          View master playbook
        </button>
      </div>
      <div className="ddw-hub-footer-note">
        Compare requires 2+ scored vendors. Master playbook requires at least 1.
      </div>
    </div>
  );
}

function LensSummary({ lens }: { lens: DdwLens }) {
  const fr = FRICTION_PHASES.find((f) => f.key === lens.frictionPhase);
  const fl = FLEX_LEVELS.find((f) => f.key === lens.flexibility);
  const filledOutcomes = lens.outcomes.filter((o) => o && o.trim());
  const feel = lens.emotional.feel;
  const avoid = lens.emotional.avoid;

  return (
    <div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">Job statement</span>
        <span className="ddw-lens-row-value">{lens.jobStatement}</span>
      </div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">Friction phase</span>
        <span className="ddw-lens-row-value">{fr?.label ?? '—'}</span>
      </div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">Outcomes</span>
        <span className="ddw-lens-row-value">
          {filledOutcomes.map((o, i) => (
            <div key={i}>
              {i + 1}. {o}
            </div>
          ))}
        </span>
      </div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">How success should feel</span>
        <span className="ddw-lens-row-value">
          <div>
            <strong style={{ color: 'var(--secondary)', fontSize: 11 }}>WANT TO FEEL: </strong>
            {feel.length > 0 ? (
              feel.map((f) => (
                <span key={f} className="ddw-report-pill is-yes" style={{ marginRight: 4 }}>
                  {f}
                </span>
              ))
            ) : (
              <span style={{ color: 'var(--textMuted)' }}>—</span>
            )}
          </div>
          <div style={{ marginTop: 6 }}>
            <strong style={{ color: 'var(--warning)', fontSize: 11 }}>WANT TO STOP: </strong>
            {avoid.length > 0 ? (
              avoid.map((a) => (
                <span key={a} className="ddw-report-pill is-warn" style={{ marginRight: 4 }}>
                  {a}
                </span>
              ))
            ) : (
              <span style={{ color: 'var(--textMuted)' }}>—</span>
            )}
          </div>
        </span>
      </div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">Flexibility requirement</span>
        <span className="ddw-lens-row-value">{fl?.label ?? '—'}</span>
      </div>
      <div className="ddw-lens-row">
        <span className="ddw-lens-row-label">Baseline annual adaptation tax</span>
        <span className="ddw-lens-row-value">
          <strong style={{ color: 'var(--primary)', fontSize: 14 }}>
            {fmtMoney(lens.tax.calculatedAnnual)}
          </strong>
        </span>
      </div>
    </div>
  );
}

function VendorCard({
  vendor,
  lens,
  config,
  onScore,
  onRemove,
  onReport,
}: {
  vendor: DdwVendor;
  lens: DdwLens;
  config: DdwConfig;
  onScore(id: string): void;
  onRemove(id: string): void;
  onReport(): void;
}) {
  if (!vendor.scored) {
    return (
      <div className="ddw-vendor-card is-unscored">
        <div>
          <div className="ddw-vendor-card-name">
            {vendor.name}
            {vendor.category && <span className="ddw-vendor-card-category">{vendor.category}</span>}
            <span className="ddw-pending-pill">Pending</span>
          </div>
          <div className="ddw-pending-hint">
            ~2 minutes — score against your lens to enable comparison.
          </div>
        </div>
        <div className="ddw-vendor-card-actions">
          <button type="button" className="ddw-sbtn" onClick={() => onScore(vendor.id)}>
            Score this vendor →
          </button>
          <button
            type="button"
            className="ddw-tbtn is-danger"
            onClick={() => {
              if (confirm(`Remove "${vendor.name}" from the workbench? This can't be undone.`)) {
                onRemove(vendor.id);
              }
            }}
            title="Remove vendor"
            aria-label={`Remove ${vendor.name}`}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  const s = vendor.score!;
  const owner = OWNERSHIP_TYPES.find((o) => o.key === s.ownership);
  const protections = s.protections.filter(Boolean).length;
  const validFits = s.outcomeFit.filter((x): x is number => x != null);
  const outcomeAvg =
    validFits.length > 0 ? (validFits.reduce((a, b) => a + b, 0) / validFits.length).toFixed(1) : '—';
  const proj = projectVendorTax(lens, s, config);
  const conf = adoptionConfidence(s, config.adoptionQuestions);
  const risk = valueAtRisk(lens, s, config);
  const confColor = conf >= 70 ? 'var(--success)' : conf >= 40 ? 'var(--warning)' : 'var(--error)';

  return (
    <div className="ddw-vendor-card is-scored">
      <div>
        <div className="ddw-vendor-card-name">
          {vendor.name}
          {vendor.category && <span className="ddw-vendor-card-category">{vendor.category}</span>}
        </div>
        <div className="ddw-vendor-card-meta">
          <span className="ddw-vendor-card-meta-item">
            <strong>{outcomeAvg}/5</strong>&nbsp;outcome
          </span>
          <span className="ddw-vendor-card-meta-item">
            <strong>{s.emotionalFit ?? '—'}/5</strong>&nbsp;emotional
          </span>
          {owner && <span className="ddw-vendor-card-meta-item">{owner.short}</span>}
          <span className="ddw-vendor-card-meta-item">
            <strong>{protections}/4</strong>&nbsp;confirmed
          </span>
          <span className="ddw-vendor-card-meta-item">
            <strong>{s.gut}/10</strong>&nbsp;gut
          </span>
        </div>
        <div
          className="ddw-vendor-card-meta"
          style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--borderLight)' }}
        >
          <span className="ddw-vendor-card-meta-item">
            Projected adaptation tax{' '}
            <strong style={{ color: 'var(--primary)' }}>{fmtMoney(proj.total)}/yr</strong>
          </span>
          <span className="ddw-vendor-card-meta-item">
            Adoption <strong style={{ color: confColor }}>{conf}%</strong>
          </span>
          <span className="ddw-vendor-card-meta-item">
            At risk <strong style={{ color: 'var(--warning)' }}>{fmtMoney(risk)}</strong>
          </span>
        </div>
      </div>
      <div className="ddw-vendor-card-actions">
        <button type="button" className="ddw-tbtn" onClick={onReport}>
          Report
        </button>
        <button type="button" className="ddw-tbtn" onClick={() => onScore(vendor.id)}>
          Edit
        </button>
        <button
          type="button"
          className="ddw-tbtn is-danger"
          onClick={() => {
            if (confirm(`Remove "${vendor.name}" from the workbench? This can't be undone.`)) {
              onRemove(vendor.id);
            }
          }}
          title="Remove vendor"
          aria-label={`Remove ${vendor.name}`}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function ResearchPanel({
  research,
  questions,
  onSubmit,
  onDismiss,
}: {
  research: DdwResearch | null;
  questions: { q1: string; q2: string; q3: string };
  onSubmit(answers: DdwResearch): void;
  onDismiss(): void;
}) {
  const [a1, setA1] = useState('');
  const [a2, setA2] = useState('');
  const [a3, setA3] = useState('');

  if (research) {
    return (
      <>
        <div className="ddw-hub-section-header">
          <span className="ddw-hub-section-title">Help us learn — thank you</span>
          <span className="ddw-hub-section-meta">submitted anonymously</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--textSecondary)', lineHeight: 1.55 }}>
          {research.dismissed
            ? 'Skipped — you can reopen this anytime by clearing site data.'
            : 'Thanks for sharing. Your answers help us improve this framework for the next buyer.'}
        </p>
      </>
    );
  }

  const handleSubmit = () => {
    onSubmit({
      q1: { question: questions.q1, answer: a1.trim() },
      q2: { question: questions.q2, answer: a2.trim() },
      q3: { question: questions.q3, answer: a3.trim() },
      submittedAt: new Date().toISOString(),
    });
  };

  return (
    <>
      <div className="ddw-hub-section-header">
        <span className="ddw-hub-section-title">Help us learn — optional</span>
        <button type="button" className="ddw-tbtn" onClick={onDismiss}>
          No thanks
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--textSecondary)', lineHeight: 1.55, marginBottom: 14 }}>
        Three short questions. Anonymous — your answers help us learn how legal teams use this
        framework. No follow-up, no pitch.
      </p>
      <div>
        <div className="ddw-field">
          <label className="ddw-field-label" style={{ fontSize: 12 }}>
            {questions.q1}
          </label>
          <input
            type="text"
            className="ddw-input"
            value={a1}
            onChange={(e) => setA1(e.target.value)}
            placeholder="(optional)"
          />
        </div>
        <div className="ddw-field">
          <label className="ddw-field-label" style={{ fontSize: 12 }}>
            {questions.q2}
          </label>
          <input
            type="text"
            className="ddw-input"
            value={a2}
            onChange={(e) => setA2(e.target.value)}
            placeholder="(optional)"
          />
        </div>
        <div className="ddw-field">
          <label className="ddw-field-label" style={{ fontSize: 12 }}>
            {questions.q3}
          </label>
          <input
            type="text"
            className="ddw-input"
            value={a3}
            onChange={(e) => setA3(e.target.value)}
            placeholder="(optional)"
          />
        </div>
        <button type="button" className="ddw-sbtn" onClick={handleSubmit}>
          Submit anonymously
        </button>
      </div>
    </>
  );
}
